"use client";

import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import styles from "./Input.module.css";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Validation / helper message. */
  hint?: string;
  invalid?: boolean;
  /** Light surface variant (for use on white sections). */
  theme?: "dark" | "light";
}

/**
 * RUBYN text input. Pill field matching the brand inputs, with optional label,
 * icons, and a built-in show/hide toggle for password fields.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, leftIcon, rightIcon, hint, invalid, theme = "dark", type = "text", id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword ? (reveal ? "text" : "password") : type;

  const wrapCls = [
    styles.field,
    styles[`t-${theme}`],
    invalid ? styles.invalid : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <div className={wrapCls}>
        {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
        <input
          id={inputId}
          ref={ref}
          type={effectiveType}
          className={styles.input}
          aria-invalid={invalid || undefined}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Hide password" : "Show password"}
          >
            {reveal ? "Hide" : "Show"}
          </button>
        ) : (
          rightIcon && <span className={styles.icon}>{rightIcon}</span>
        )}
      </div>
      {hint && (
        <span className={[styles.hint, invalid ? styles.hintInvalid : ""].filter(Boolean).join(" ")}>
          {hint}
        </span>
      )}
    </div>
  );
});

/* ---- Reusable validation criteria (ported from the old Input) ---- */

export function validateEmail(text: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!re.test(text) || text.length > 254) return false;
  const [local] = text.split("@");
  return local.length <= 64;
}

export function validateNonEmpty(text: string): boolean {
  return text.trim().length > 0;
}

export function validatePassword(text: string): boolean {
  return (
    text.length >= 8 &&
    /[a-z]/.test(text) &&
    /[A-Z]/.test(text) &&
    /\d/.test(text)
  );
}
